/**
 * Creator fit, banded exactly as web-v2 `src/lib/discovery-tier-labels.ts`
 * does (the operator and the client must never see two different verdicts
 * for one creator). The raw pipeline tier (gold/silver/bronze) is never
 * rendered — it is only an input here.
 */
export type FitBand = 'high' | 'medium' | 'looser' | 'possible' | 'low' | 'screened' | 'unrated';

/** The qualifying bar (backend silverKeywordFloor.judge). */
export const QUALIFYING_FIT_SCORE = 60;
/** Bottom of the "possible" band (backend POSSIBLE_BAND_FLOOR). */
export const POSSIBLE_FIT_SCORE = 50;

export function fitBand(input: {
  fitTier?: string | null;
  fitScore?: number | null;
  affinityScore?: number | null;
  audienceSource?: string | null;
  unrated?: boolean | null;
}): FitBand {
  const { fitTier: tier, fitScore: displayScore, affinityScore, audienceSource, unrated } = input;
  if (unrated) return 'unrated';
  const relevance = typeof affinityScore === 'number' && Number.isFinite(affinityScore) ? affinityScore : null;
  if (tier === 'bronze') {
    if (relevance == null) return 'screened';
    return relevance >= POSSIBLE_FIT_SCORE ? 'possible' : 'low';
  }
  if (relevance == null) {
    if (typeof displayScore !== 'number' || !Number.isFinite(displayScore)) {
      return tier === 'gold' ? 'high' : tier === 'silver' ? 'medium' : 'screened';
    }
    const effective = audienceSource === 'verified' ? displayScore : displayScore / 0.7;
    return effective >= 70 ? 'high' : 'medium';
  }
  if (relevance >= 70) return 'high';
  if (relevance >= QUALIFYING_FIT_SCORE) return 'medium';
  return 'looser';
}

export const FIT_BAND_LABEL: Record<FitBand, string> = {
  high: 'Strong match',
  medium: 'Good match',
  looser: 'Looser match',
  possible: 'Possible match',
  low: 'Not a match',
  screened: 'Not yet assessed',
  unrated: 'Not enough data',
};

/** The score shown and sorted on: relevance, rounded; null sorts last. */
export function fitScore(input: { affinityScore?: number | null; unrated?: boolean | null }): number | null {
  if (input.unrated) return null;
  const v = input.affinityScore;
  return typeof v === 'number' && Number.isFinite(v) ? Math.round(v) : null;
}

/** The three bands a reviewer filters on. */
export type FitFilter = 'strong' | 'good' | 'other';
export function fitFilterOf(band: FitBand): FitFilter {
  if (band === 'high') return 'strong';
  if (band === 'medium') return 'good';
  return 'other';
}
